import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, RefreshCw, Route, Truck } from 'lucide-react';
import DriverSettingsDrawer from './DriverSettingsDrawer';
import PriorityDeliveriesTable from './PriorityDeliveriesTable';
import RoutePlanBoard from './RoutePlanBoard';
import RoutePlanMap from './RoutePlanMap';
import RoutePlannerSidebar from './RoutePlannerSidebar';
import {
  API_URL,
  driverPreferencePatch,
  normalizeDriverSettings,
  selectedDriversPayload,
  todayIso,
} from './routePlannerUtils';

function AdminRoutePlanner({ setActiveComponent, userId }) {
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [config, setConfig] = useState({ zones: [], districts: [], defaults: {} });
  const [preview, setPreview] = useState(null);
  const [driverSettings, setDriverSettings] = useState({});
  const [plan, setPlan] = useState(null);
  const [strategy, setStrategy] = useState('use_all_available');
  const [serviceMinutes, setServiceMinutes] = useState(6);
  const [maxPackagesDefault, setMaxPackagesDefault] = useState(25);
  const [useGoogle, setUseGoogle] = useState(true);
  const [driversOpen, setDriversOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [moveDrafts, setMoveDrafts] = useState({});

  const fetchConfig = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/admin/route-planner/config`);
    setConfig(data);
    setDriverSettings(normalizeDriverSettings(data.drivers || []));
    setServiceMinutes(data.defaults?.service_minutes || 6);
    setMaxPackagesDefault(data.defaults?.max_packages || 25);
  }, []);

  const fetchPreview = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/admin/route-planner/preview`, {
      params: { date: selectedDate },
    });
    setPreview(data);
  }, [selectedDate]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const selectedCount = useMemo(
    () => Object.values(driverSettings).filter((driver) => driver.selected).length,
    [driverSettings]
  );

  function updateDriver(userIdKey, patch) {
    setDriverSettings((current) => ({
      ...current,
      [userIdKey]: { ...current[userIdKey], ...patch },
    }));
  }

  async function saveDriverPreferences(driver) {
    await axios.patch(
      `${API_URL}/api/admin/route-planner/drivers/${driver.driver_id}`,
      driverPreferencePatch(driver, maxPackagesDefault)
    );
    setMessage(`Preferencias guardadas para ${driver.name}`);
  }

  async function togglePriority(delivery) {
    await axios.patch(`${API_URL}/api/admin/route-planner/deliveries/${delivery.id}/priority`, {
      priority: !delivery.priority,
      priority_level: !delivery.priority ? 1 : 0,
    });
    await fetchPreview();
  }

  async function optimizePlan() {
    setLoading(true);
    setMessage('');
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/route-plans/optimize`, {
        delivery_date: selectedDate,
        admin_user_id: userId,
        strategy,
        service_minutes: Number(serviceMinutes || 6),
        max_packages_default: Number(maxPackagesDefault || 25),
        use_google: useGoogle,
        drivers: selectedDriversPayload(driverSettings, maxPackagesDefault),
      });
      setPlan(data);
      setMessage(`Plan creado: ${data.summary?.assigned_stops || 0} entregas asignadas`);
    } catch (err) {
      setMessage(err.response?.data?.error || 'No se pudo optimizar la ruta');
    } finally {
      setLoading(false);
    }
  }

  async function loadLatestPlan() {
    if (!preview?.latest_plan?.id) return;
    const { data } = await axios.get(`${API_URL}/api/admin/route-plans/${preview.latest_plan.id}`);
    setPlan(data);
  }

  async function refreshPlan() {
    if (!plan?.id) return;
    const { data } = await axios.get(`${API_URL}/api/admin/route-plans/${plan.id}`);
    setPlan(data);
  }

  async function moveStop(stop, currentRouteId, delta) {
    await axios.post(`${API_URL}/api/admin/route-plans/${plan.id}/move-stop`, {
      stop_id: stop.id,
      target_route_id: currentRouteId,
      target_sequence: Math.max(1, stop.sequence + delta),
    });
    await refreshPlan();
  }

  async function moveStopToRoute(stop) {
    const draft = moveDrafts[stop.id];
    if (!draft?.target_route_id) return;
    const { data } = await axios.post(`${API_URL}/api/admin/route-plans/${plan.id}/move-stop`, {
      stop_id: stop.id,
      target_route_id: Number(draft.target_route_id),
      target_sequence: Number(draft.target_sequence || 9999),
    });
    setPlan(data);
  }

  async function publishPlan() {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/route-plans/${plan.id}/publish`);
      setPlan(data);
      setMessage('Rutas publicadas para conductores');
      await fetchPreview();
    } catch (err) {
      setMessage(err.response?.data?.error || 'No se pudo publicar');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        loading={loading}
        selectedCount={selectedCount}
        optimizePlan={optimizePlan}
        setActiveComponent={setActiveComponent}
      />

      <div className="grid grid-cols-1 gap-4 p-4 xl:grid-cols-[320px_1fr]">
        <RoutePlannerSidebar
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          strategy={strategy}
          setStrategy={setStrategy}
          serviceMinutes={serviceMinutes}
          setServiceMinutes={setServiceMinutes}
          maxPackagesDefault={maxPackagesDefault}
          setMaxPackagesDefault={setMaxPackagesDefault}
          useGoogle={useGoogle}
          setUseGoogle={setUseGoogle}
          preview={preview}
          selectedCount={selectedCount}
          onOpenDrivers={() => setDriversOpen(true)}
          onLoadLatestPlan={loadLatestPlan}
        />

        <main className="space-y-4">
          {message && (
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
              {message}
            </div>
          )}
          <RoutePlanMap plan={plan} preview={preview} />
          <PriorityDeliveriesTable
            selectedDate={selectedDate}
            deliveries={preview?.deliveries || []}
            onTogglePriority={togglePriority}
          />
          {plan ? (
            <RoutePlanBoard
              plan={plan}
              loading={loading}
              publishPlan={publishPlan}
              moveStop={moveStop}
              moveStopToRoute={moveStopToRoute}
              moveDrafts={moveDrafts}
              setMoveDrafts={setMoveDrafts}
            />
          ) : (
            <EmptyPlanState />
          )}
        </main>
      </div>

      <DriverSettingsDrawer
        open={driversOpen}
        onClose={() => setDriversOpen(false)}
        driverSettings={driverSettings}
        updateDriver={updateDriver}
        saveDriverPreferences={saveDriverPreferences}
        zones={config.zones || []}
        districts={config.districts || []}
      />
    </div>
  );
}

function Header({ loading, selectedCount, optimizePlan, setActiveComponent }) {
  return (
    <div className="sticky top-0 z-[1200] border-b border-slate-200 bg-white">
      <div className="px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => setActiveComponent('OptionsAdmin')}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </button>
          <div className="flex items-center gap-2">
            <Route className="h-6 w-6 text-brand-primary" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Planificador de rutas</h1>
              <p className="text-sm text-slate-500">Asignacion masiva para Lima</p>
            </div>
          </div>
          <button
            onClick={optimizePlan}
            disabled={loading || selectedCount === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Optimizar
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyPlanState() {
  return (
    <section className="flex min-h-[260px] items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center">
      <div>
        <Truck className="mx-auto mb-3 h-10 w-10 text-slate-400" />
        <h2 className="text-lg font-bold text-slate-900">Sin plan cargado</h2>
        <p className="text-sm text-slate-500">Selecciona conductores y optimiza para crear un borrador.</p>
      </div>
    </section>
  );
}

export default AdminRoutePlanner;
